<template>
    <main id="files-gallery">
        <span v-if="files.length === 0 && search.trim() === ''" class="alert alert-info mb-4 mx-3">
            {{ t('filesGallery-no_files_text') }}
            <strong> {{ t('filesGallery-no_files_cta') }} </strong>
        </span>
        <confirm
            id="delete-file-confirm"
            ref="deleteFileConfirm"
            @confirm="confirmDelete"
        >
            <template v-slot:title>{{ t('filesGallery-delete_confirm-title') }}</template>
            <template v-slot:body>
                <p v-html="t('filesGallery-delete_confirm-question', [fileToDelete?.name])"></p>
                <p>{{ t('filesGallery-delete_confirm-warning') }}</p>
                <p class="text-warning">{{ t('filesGallery-delete_confirm-irreversible') }}</p>
            </template>
        </confirm>
        <div class="coluna col-md-6">
            <div v-if="files.length !== 0" class="input-group mb-3">
                <input v-model="search" :placeholder="t('filesGallery-search_placeholder')"
                    :aria-label="t('filesGallery-search_placeholder')" type="text" class="form-control"
                    aria-describedby="cleansearch" />
                <div class="input-group-append">
                    <button @click="search = ''" class="btn btn-outline-secondary" type="button" id="cleansearch">
                        <i class="fa fa-eraser"></i>
                    </button>
                </div>
            </div>
            <filter-bar
                v-show="files.length !== 0"
                ref="filter-bar"
                class="filter-bar"
                :has-period-filter="false"
                :has-branch-filter="false"
                :allowReset="true"
                :updateQueryString="false"
                @changed="loadFilters"
                :filters="extraFilters"
            />
            <div class="files-upload"
                @dragover.prevent="onDragOver"
                @dragenter.prevent="onDragEnter"
                @dragleave.prevent="onDragLeave"
                @drop.prevent="onDrop"
            >
                <div v-if="!uploadLoading" class="upload-area mt-4" @drop.prevent="handleDrop" @dragover.prevent @click="triggerFileInput">
                    <input type="file" @change="handleFileUpload" multiple hidden ref="fileInput" />
                    <p>{{ t('filesGallery-upload_area') }}</p>
                </div>
                <div v-else class="loading mt-4">
                    <div class="progress">
                        <div class="progress-bar" role="progressbar" :style="{ width: uploadProgress + '%' }" :aria-valuenow="uploadProgress" aria-valuemin="0" aria-valuemax="100">{{ uploadProgress }}%</div>
                    </div>
                </div>
                <span v-if="error" class="alert alert-danger mt-4">{{ error }}</span>
                <ul class="files-list" v-if="files.length">
                    <li v-for="file in filteredFiles" :key="file.id">
                        <a id="file-preview" target="_blank" :href="file.url">
                            <img v-if="file.type.startsWith('image/')" loading="lazy" :src="file.url" :alt="`${file.name} image`" />
                            <span class="fa fa-file-video-o" v-else-if="file.type.startsWith('video/')" />
                            <span class="fa fa-file-audio-o" v-else-if="file.type.startsWith('audio/')" />
                            <span class="fa fa-file-pdf-o" v-else-if="file.type === 'application/pdf'" />
                            <span class="fa fa-file" v-else />
                        </a>
                        <article id="file-info">
                            <h2>{{ file.name }}</h2>
                            <div>
                                <span>
                                    <human-date
                                        :date="file.upload_date"
                                        :format="t('dates-format_date_time_comma')"
                                    />
                                    - {{ file.size_mb }} MB
                                </span>
                                <div class="actions">
                                    <button @click="copyUrl(file.url)" class="btn btn-primary ml-4">{{ t('filesGallery-copy_url') }}</button>
                                    <button @click="openDeleteConfirm(file)" class="btn btn-danger ml-2">{{ t('filesGallery-delete_button') }}</button>
                                </div>
                            </div>
                        </article>
                    </li>
                </ul>
            </div>
        </div>
        <span v-if="filteredFiles.length === 0 && search.trim() !== ''" class="alert alert-info" v-html="t('filesGallery-no_results_search', [search])"></span>
        <span v-else-if="filteredFiles.length === 0 && this.filters" class="alert alert-info">
            {{ t('filesGallery-no_results_filter') }}
        </span>
    </main>
</template>

<style scoped lang="less">
@media (max-width: 768px) and (min-width: 320px) {
    main#files-gallery {
        .actions {
            gap: 10px;
            display: flex;
            flex-direction: column;
        }
    }
}
@media (max-width: 1000px) and (min-width: 768px) {
    main#files-gallery {
        .coluna {
            max-width: 100%;
        }
    }
}
@media (max-width: 1320px) and (min-width:1000px) {
    main#files-gallery {
        .coluna {
            max-width: 70%;
        }
    }
}
main#files-gallery {
    div {
        .files-upload {
            .upload-area {
                display: flex;
                justify-content: center;
                justify-items: center;
                border: 2px dashed #ccc;
                margin-bottom: 20px;
                padding: 40px;
                text-align: center;
                cursor: pointer;
                transition: background-color 0.3s;

                &:hover,
                &:active,
                &:focus,
                &.dragover {
                    background-color: #dedede;
                }

                p {
                    margin: 0;
                }
            }

            .loading {
                margin-left: 45%;
                margin-right: 45%;

                .progress {
                    width: 100%;
                    height: 20px;
                    background-color: #ddd;
                    border-radius: 5px;
                    overflow: hidden;

                    .progress-bar {
                        height: 100%;
                        background-color: #007bff;
                        text-align: center;
                        color: white;
                        white-space: nowrap;
                        transition: width 0.4s ease;
                    }
                }
            }

            ul.files-list {
                width: 100%;
                margin-top: 20px;
                list-style: none;
                padding: 0;

                li {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 10px;
                    border: 1px solid #ccc;
                    border-radius: 5px;
                    margin-bottom: 10px;
                    background-color: #fff;

                    #file-preview {
                        img {
                            width: 100%;
                            height: 100%;
                            object-fit: contain;
                        }
                    }

                    #file-preview:hover,
                    #file-preview:focus {
                        border: 1px solid #007bff;
                    }

                    article#file-info {
                        flex-grow: 1;
                        margin-left: 10px;

                        h2 {
                            margin: 0;
                            font-size: 1rem;
                        }

                        div {
                            display: flex;
                            justify-content: space-between;
                            align-items: center;

                            span {
                                font-size: 0.8rem;
                                color: #666;
                            }

                            button {
                                margin-left: 10px;
                            }
                        }
                    }

                }
            }
        }
    }

    ul {
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 8px;

        li {
            display: flex;
            gap: 10px;

            a#file-preview {
                width: 60px;
                height: 60px;
                overflow: hidden;
                display: flex;
                justify-content: center;
                align-items: center;
                border: 1px solid #ccc;
                cursor: pointer;

                img {
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                }

                span {
                    font-size: 2rem;
                }
            }

            article#file-info {
                display: flex;
                flex-direction: column;
                gap: 5px;

                div {
                    span {
                        font-size: 0.8rem;
                    }

                    button {
                        align-self: flex-end;
                    }
                }
            }
        }
    }
}
</style>

<script>
import keviApi from "../keviApi";
import FilterBar from "../components/reports/filter-bar";
import HumanDate from "../components/human-date/human-date";

export default {
    name: "files-gallery",
    components: {
        FilterBar,
        HumanDate
    },
    data() {
        return {
            files: [],
            uploadLoading: false,
            uploadProgress: 0,
            search: "",
            fileType: null,
            error: null,
            filters: null,
            fileToDelete: null,
            extraFilters: [],
        };
    },
    computed: {
        filteredFiles() {
            if (this.search.trim() === "" && !this.filters) {
                return this.files;
            }

            const searchFilter = this.search.trim() === "" ? this.files :
                this.files.filter(file => file.name.toLowerCase().includes(this.search.toLowerCase()));

            if (this.filters && this.filters.length > 0) {
                return searchFilter.filter(file => this.filters.includes(file.type));
            }
            return searchFilter;
        }
    },
    watch: {
        search(newSearch) {
            this.search = newSearch;
            this.filteredFiles;
        },
    },
    created: function () {
        document.getElementsByTagName("title")[0].innerText =
            this.t("filesGallery-page_title");
    },
    mounted() {
        const options = keviApi.getOptions("files-gallery");
        this.$http(options).then(response => {
            this.files = Object.values(response.data.files);
        });
        const filterBar = this.$refs["filter-bar"];
        this.extraFilters.push({
            component: "selectbox",
            label: this.$root.t('filter_bar-file_type'),
            removable: false,
            options: [
                {
                    value: ["image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp", "image/svg+xml", "image/avif"],
                    label: this.$root.t('filter_bar-image')
                },
                {
                    value: ["application/pdf"],
                    label: this.$root.t('filter_bar-pdf')
                },
                {
                    value: ["video/mp4", "video/avi", "video/mov", "video/wmv", "video/flv", "video/webm", "video/mkv", "video/x-matroska"],
                    label: this.$root.t('filter_bar-video')
                },
                {
                    value: ["audio/mpeg", "audio/wav", "audio/ogg", "audio/mp3", "audio/flac", "audio/aac"],
                    label: this.$root.t('filter_bar-audio')
                },
                {
                    value: ["application/zip","application/rar","application/x-rar-compressed","text/plain","application/msword","application/vnd.openxmlformats-officedocument.wordprocessingml.document","application/vnd.ms-excel","application/vnd.openxmlformats-officedocument.spreadsheetml.sheet","application/vnd.ms-powerpoint","application/vnd.openxmlformats-officedocument.presentationml.presentation","text/csv"],
                    label: this.$root.t('filter_bar-other')
                },
            ],
            name: "fileType",
        });

        this.$nextTick(() => {
          filterBar.addFilter("fileType");
        });
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        onDragOver(event) {
            event.preventDefault();
        },
        onDragEnter(event) {
            event.preventDefault();
            event.target.classList.add("dragover");
        },
        onDragLeave(event) {
            event.target.classList.remove("dragover");
        },
        onDrop(event) {
            event.preventDefault();
            event.target.classList.remove("dragover");
        },
        async copyUrl(url) {
            if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(url);
            } else {
            const textArea = document.createElement("textarea");
            textArea.value = url;
            textArea.style.position = "fixed";
            textArea.style.top = "0";
            textArea.style.left = "-9999px";
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
                document.execCommand("copy");
            } catch (err) {
                console.error("Fallback: Oops, unable to copy", err);
            }
            document.body.removeChild(textArea);
            }
            this.$store.dispatch(
                "notification/success",
                this.t("filesGallery-notifications-url_copied"),
            );
        },
        triggerFileInput() {
            this.$refs.fileInput.click();
        },
        handleFileUpload(event) {
            const files = event.target.files;
            this.upload(files);
        },
        handleDrop(event) {
            const files = event.dataTransfer.files;
            this.upload(files);
        },
        upload(files) {
            this.error = null;
            this.uploadLoading = true;
            this.uploadProgress = 0;
            const formData = new FormData();
            const oversizedFiles = [];

            for (let i = 0; i < files.length; i++) {
                if (files[i].size / (1024 * 1024) > 20) {
                    oversizedFiles.push(files[i].name);
                } else {
                    formData.append("files[]", files[i]);
                }
            }

            if (oversizedFiles.length > 0) {
                this.error = `${this.t("filesGallery-notifications-oversized_files")} ${oversizedFiles.join(", ")}`;
                this.uploadLoading = false;
                this.uploadProgress = 0;
                return;
            }

            const xhr = new XMLHttpRequest();
            xhr.open("POST", keviApi.getOptions("storage/upload").url, true);
            xhr.setRequestHeader("Authorization", keviApi.getAuthHeader().Authorization);

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    this.uploadProgress = Math.round((event.loaded * 100) / event.total);
                }
            };

            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    const response = JSON.parse(xhr.responseText);
                    response.data.forEach((fileUrl, index) => {
                        this.files.unshift({
                            id: Date.now() + index,
                            name: files[index].name,
                            type: files[index].type,
                            url: fileUrl,
                            upload_date: new Date().toISOString(),
                            size_mb: (files[index].size / (1024 * 1024)).toFixed(2)
                        });
                    });
                    this.error = null;
                    this.uploadLoading = false;
                    this.uploadProgress = 0;
                    this.$store.dispatch(
                        "notification/success",
                        this.t("filesGallery-notifications-upload_success"),
                    );
                } else {
                    const errorResponse = JSON.parse(xhr.responseText);
                    this.error = errorResponse.data.message || this.t("filesGallery-notifications-upload_error_message");
                    this.uploadLoading = false;
                    this.uploadProgress = 0;
                    this.$store.dispatch(
                        "notification/error",
                        this.t("filesGallery-notifications-upload_error_title"),
                    );
                }
            };

            xhr.onerror = () => {
                this.error = this.t("filesGallery-notifications-upload_error_message");
                this.uploadLoading = false;
                this.uploadProgress = 0;
                this.$store.dispatch(
                    "notification/error",
                    this.t("filesGallery-notifications-upload_error_title"),
                );
            };

            xhr.send(formData);
        },
        loadFilters(filter) {
            if (filter !== undefined) {
                this.filters = filter.fileType;
                this.filteredFiles;
            } else {
                this.filters = null;
                this.filteredFiles;
            }
        },
        openDeleteConfirm(file) {
            this.fileToDelete = file;
            this.$refs.deleteFileConfirm.open();
        },
        async confirmDelete() {
            if (!this.fileToDelete) return;

            try {
                const options = keviApi.getOptions(`storage/delete/${this.fileToDelete.id}`, "DELETE");
                await this.$http(options);

                const index = this.files.findIndex(f => f.id === this.fileToDelete.id);
                if (index !== -1) {
                    this.files.splice(index, 1);
                }

                this.$store.dispatch(
                    "notification/success",
                    this.t("filesGallery-notifications-delete_success")
                );
            } catch (error) {
                this.$store.dispatch(
                    "notification/error",
                    this.t("filesGallery-notifications-delete_error")
                );
                console.error("Error deleting file:", error);
            } finally {
                this.fileToDelete = null;
            }
        }
    }
};
</script>
